/**
 * CalendarPage — container for the PA Calendar (PROD-1190).
 *
 * Reads auth + role, fetches the selected window's appointments scoped to the
 * in-scope providers (physician → self; nurse → assigned providers), maps them
 * to DS calendar events, and renders the Signal DS PACalendarPage frame. Around
 * the active DS grid it composes the ported PM chrome:
 *   - CalendarToolbar    — tabs · search · practice selector · settings ·
 *                          Create menu (New Appointment) ·
 *                          specialty filter + provider bulk-select.
 *   - ProviderStrip      — provider cards (with slot counts) above the grid.
 *   - CalendarSubToolbar — date picker · status/invoice/location filters ·
 *                          view toggle.
 *   - ViewAppointmentPopup — opened on event-click; its "Open encounter"
 *                          action navigates to /encounter/:appointmentId
 *                          (the PA-DIFF that replaces the direct navigate).
 *   - CreateAppointmentPanel — the rail the Create menu opens. The PA calendar
 *                          has NO block availability (supervisor ruling, 21 Oct):
 *                          no Create item, no block-tile edit, no Block panel;
 *                          blocks from the API still draw, read-only.
 *
 * Role-based behavior (kept from Slice 3):
 *   - Physician: doctors = [self], provider filter = [self]; default view Day.
 *   - Nurse: doctors = assigned providers (useAssignedProviders), provider
 *     filter = their ids; default view Providers.
 *
 * Filter state (specialty / status / invoice / location / provider) lives in
 * the scheduling-store (the sub-toolbar + toolbar read it directly). Date /
 * view / practice stay as page state because they drive the DS-grid
 * query window directly.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ModalYesNo, BrandLoading, addMinutesISO } from '@scripsteam/scrips-signal-ds';
import { formatClockTime } from '@/shared/lib/time-format';
import { useAuthStore } from '@/auth/auth-store';
import { useRole } from '@/auth/use-role';
import { evaluatePermissions } from '@/auth/permission-gate';
import { useAppointments } from './services/use-appointments';
import { useCalendarSlots } from './services/use-calendar-slots';
import { useAssignedProviders } from './services/use-assigned-providers';
import { usePractitionerPractices } from './services/use-practitioner-practices';
import { useOrgLocations } from './services/use-org-locations';
import { useExamRooms } from './services/use-exam-rooms';
import { usePracticeWorkingHours } from './services/use-practice-working-hours';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useRescheduleAppointment } from './services/use-reschedule-appointment';
import { rangeFor } from './lib/calendar-range';
import { routeAppointmentDrop } from './lib/drop-routing';
import { dateFilterToCalendarView } from './lib/date-filter-mapping';
import { useSchedulingRealtime } from './realtime/use-scheduling-realtime';
import { AppointmentSearch } from './components/calendar/appointment-search';
import {
  specialityDisplay,
  mapAppointmentsToDayList,
  mapAppointmentsToWeekTiles,
  hoursForDayList,
  providersFromCalendarSlots,
  appointmentProviderIds,
  filterAppointmentsByStatus,
  filterAppointmentsByLocation,
  filterAppointmentsByInvoiceStatus,
  providerMatchesSpecialties,
  specialtiesFromCalendarSlots,
  type DayListRow,
  type ProviderSummary,
  type CalendarProviderSummary,
} from './lib/calendar-mapping';
import type { AppointmentActionId } from './lib/appointment-actions';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { useSchedulingStore, type CalendarView as StoreCalendarView } from './store/scheduling-store';
import type { CalendarViewMode, CalendarPractice } from './lib/calendar-view-mode';
// The calendar chrome + grids are the Signal DS PACalendar* family (graduated
// from this app's PROD-1190 PM port). The app keeps the data layer and fills
// the family's lift seams from `./components/calendar/calendar-seams`.
import {
  PACalendarPage,
  PACalendarByDoctor,
  PACalendarByRoom,
  buildMonthGrid,
  toLocalISO,
  groupRowsByDoctor,
  groupRowsByRoom,
  type PACalendarDayColumn,
  type PACalendarMonthEvent,
} from '@scripsteam/scrips-signal-ds';
import {
  CalendarToolbarRow,
  CalendarPeriodControls,
  CalendarViewSwitch,
} from './components/calendar/calendar-toolbar-row';
import { CalendarFilterPill } from './components/calendar/calendar-filter-pill';
import { CalendarProviderChips } from './components/calendar/calendar-provider-chips';
import { CalendarSkeleton } from './components/calendar/calendar-skeleton';
import { CalendarDayAgenda } from './components/calendar/calendar-day-agenda';
import { CalendarWeekGrid } from './components/calendar/week-grid/calendar-week-grid';
import { WEEK_HOUR_PX } from './components/calendar/week-grid/week-layout';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { CalendarCreateMenu } from './components/calendar/calendar-create-menu';
import { CalendarSpecialtyRow } from './components/calendar/calendar-specialty-row';
import { CalendarScopeDropdown } from './components/calendar/calendar-scope-dropdown';
import { PageHeader } from '@/shared/ui/components/page-header';
import {
  renderSlotsPopover,
  useDayListSeams,
  useSubToolbarFilters,
} from './components/calendar/calendar-seams';
import {
  ViewAppointmentPopup,
  type ViewAppointmentTarget,
} from './components/appointments/view-appointment-popup';
import { CreateAppointmentPanel } from './components/appointments/create-appointment-panel';
import { CalendarEditPanel, type CalendarEditTarget } from './components/calendar/calendar-edit-panel';
import { CancelAppointmentDialog } from './components/appointments/cancel-appointment-dialog';
import { RescheduleAppointmentDialog } from './components/appointments/reschedule-appointment-dialog';
import { AddNoteDialog } from './components/appointments/add-note-dialog';
import { PostVisitSummaryDialog } from '@/patients/components/chart/post-visit-summary-dialog';
import type {
  DateFilter,
  SlotResponse,
  CreateAppointmentProfileRequest,
  AppointmentResponse,
} from '@/types/scheduling-api';
import type { ListAppointmentItem } from '@/types/appointment-api';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { applySpecialityOverride } from './lib/apply-speciality-override';
import { buildFollowUpInitialFromRow } from './lib/build-follow-up-from-row';
import { buildFollowUpInitialFromAppointment, followUpSeedDateISO } from './lib/build-follow-up-from-appointment';
import { buildProfileFormValues } from './lib/build-profile-form-values';
import { encounterNavStateFor } from './lib/encounter-nav-state';
import { showsToday } from './lib/calendar-period';
import { formatMonthLabel } from './lib/month-label';
import { monthDayData } from './lib/month-day-data';
import { CalendarMonthGrid } from './components/calendar/calendar-month-grid';
import { useScrollToNow } from './lib/use-scroll-to-now';
import { practiceNow } from '@/shared/lib/practice-time';

/** Today as YYYY-MM-DD in local time. */
function todayIso(): string {
  const now = practiceNow();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Format a local Date as YYYY-MM-DD. */
function toIso(dt: Date): string {
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Parse a YYYY-MM-DD string into a local Date (no UTC drift). */
function parseIso(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// The sub-toolbar + view-toggle speak DateFilter ('Day'|'Week'|'Month'|
// 'Room'|'Doctor'); the grid speaks CalendarViewMode. Map 1:1 — every
// DateFilter now has a ported PM grid (Room → ByRoomCalendar, PROD-1190).
function viewModeToDateFilter(v: CalendarViewMode): DateFilter {
  if (v === 'week') return 'Week';
  if (v === 'month') return 'Month';
  if (v === 'providers') return 'Doctor';
  if (v === 'room') return 'Room';
  return 'Day';
}
function dateFilterToViewMode(f: DateFilter): CalendarViewMode {
  if (f === 'Week') return 'week';
  if (f === 'Month') return 'month';
  if (f === 'Doctor') return 'providers';
  if (f === 'Room') return 'room';
  return 'day';
}

/** Initials for a provider name → ProviderStrip card avatar fallback. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// The time grids' geometry: the PA Week grid 80 px an hour (`WEEK_HOUR_PX`), the Signal DS By doctor / By room 60;
// default hours 8a-5p.
const DS_WEEK_HOUR_HEIGHT = WEEK_HOUR_PX;
const DS_DOCTOR_ROOM_HOUR_HEIGHT = 60;
const DS_DEFAULT_GRID_START_HOUR = 8;
const DS_DEFAULT_GRID_END_HOUR = 17;

export function CalendarPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { isNurse } = useRole();
  // Signal DS PACalendar* lift seams — the app halves (mutation, permission, store).
  const dayListSeams = useDayListSeams();
  // The `dayAgenda` switch (`?x=-day-agenda`) is still read, and only reported on the body (`data-day-agenda`): both of
  // its states draw the Practice calendar's Day now (PROD-3307 C5, PM reference 21 Oct). The switch's default is not
  // this page's to change (`encounter-flags.ts`), so the plumbing stays for the owner's final switch commit.
  const [dayAgenda] = useState(() => isEncounterFlagOn('dayAgenda'));
  const subToolbarFilters = useSubToolbarFilters();

  // The physician's own provider id (= user.id / doctorSetupId).
  const selfId = user?.practitionerId ?? user?.id;

  // Calendar date + view are URL-synced (`?date=YYYY-MM-DD&view=Day`), mirroring
  // PM's scheduling-page.tsx — so the appointment panel's Back button (PROD-1712)
  // can return to the exact day/view the user was on, and calendar links are
  // shareable (PROD-1714c). Lazy-init from the URL; fall back to today / Day.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get('view');
  const [date, setDate] = useState<string>(() => {
    const raw = searchParams.get('date');
    return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : todayIso();
  });
  const [view, setView] = useState<CalendarViewMode>(() =>
    // Day for every role, the nurse included (PM `scheduling-page.tsx`: Day unless the URL names a view; PROD-3307 C3).
    urlView ? dateFilterToViewMode(urlView as DateFilter) : 'day',
  );
  function handleView(v: CalendarViewMode) {
    setView(v);
  }

  // Mirror date/view into the URL (replace — paging days shouldn't spam
  // browser history). This is what lets the edit-panel's in-app Back restore
  // the exact day/view the user was on (PROD-1712 `from=`).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('date', date);
        next.set('view', viewModeToDateFilter(view));
        return next;
      },
      { replace: true },
    );
  }, [date, view, setSearchParams]);

  // Practice selector — the user's practices.
  const { data: practiceList } = usePractitionerPractices(selfId);
  const practices = useMemo<CalendarPractice[]>(
    () =>
      (practiceList ?? [])
        .filter((p): p is typeof p & { practiceSetupId: string } => !!p.practiceSetupId)
        .map((p) => ({ id: p.practiceSetupId, name: p.practiceName ?? 'Practice' })),
    [practiceList],
  );

  // Selected practice — PERSISTED in the scheduling-store (user-scoped) so the
  // choice survives navigating away and back, matching Flutter (which keeps the
  // selected practice across pages). PROD-1190 fix: it was local component
  // state that reset on unmount.
  const practiceId = useSchedulingStore((s) => s.selectedPracticeId);
  const setPracticeId = useSchedulingStore((s) => s.setSelectedPracticeId);
  const setActivePractice = useAuthStore((s) => s.setActivePractice);
  useEffect(() => {
    if (practices.length === 0) return;
    const ids = practices.map((p) => p.id);
    // Keep a valid persisted selection; only (re)default when there's none or
    // the stored practice is no longer in the user's list.
    if (practiceId && ids.includes(practiceId)) return;
    const primaryId = (practiceList ?? []).find((p) => p.isPrimary)?.practiceSetupId;
    const next =
      user?.practiceId && ids.includes(user.practiceId)
        ? user.practiceId
        : primaryId ?? practices[0].id;
    setPracticeId(next ?? null);
  }, [practices, practiceList, practiceId, setPracticeId, user?.practiceId]);

  const effectivePracticeId = practiceId ?? user?.practiceId;

  // Lock the app-wide active practice to the calendar's selected practice (PROD-1478), the
  // same as the Today worklist. The api-client PracticeId header defaults to `user.practiceId`,
  // so without this an encounter opened from the calendar (a non-login practice selected) would
  // scope every call to the login practice → CreateEncounter 400. Mirrors Flutter's persisted
  // global selected practice; per-call overrides (e.g. /Book) still win.
  useEffect(() => {
    if (!effectivePracticeId || effectivePracticeId === user?.practiceId) return;
    const p = (practiceList ?? []).find((x) => x.practiceSetupId === effectivePracticeId);
    setActivePractice({ practiceSetupId: effectivePracticeId, practiceName: p?.practiceName ?? null });
  }, [effectivePracticeId, user?.practiceId, practiceList, setActivePractice]);

  // Nurse → assigned provider ids.
  const assignedParams = useMemo(() => {
    if (!isNurse || !effectivePracticeId) return null;
    return { practiceId: effectivePracticeId, date };
  }, [isNurse, effectivePracticeId, date]);
  const { data: assignedProviderIds } = useAssignedProviders(assignedParams);

  // The Month request carries the chosen statuses and locations server-side (PM `monthAppointmentsParams`): its
  // response caps each day at 3, so the client cannot filter it without losing rows and the "+N more" count. The
  // request `status` field is `Coding[]`, display from the status list (code as the fallback), as PM sends it.
  const { data: statusList } = useStatusList();
  const monthStatusCodes = subToolbarFilters.selectedStatusCodes;
  const monthLocationIds = subToolbarFilters.selectedLocationIds;
  const monthServerFilters = useMemo(
    () =>
      view === 'month'
        ? {
            status: monthStatusCodes.map((code) => ({
              code,
              display: statusList?.find((st) => st.code === code)?.display ?? code,
            })),
            location: monthLocationIds,
          }
        : null,
    [view, monthStatusCodes, monthLocationIds, statusList],
  );

  const queryParams = useMemo(() => {
    if (!user?.organizationId || !effectivePracticeId) return null;
    // A nurse's request waits for her assigned providers (PROD-3307 C1), the Today page's rule
    // (`lib/worklist-provider-scope.ts`, `null` while loading): sent before the list arrives, `?? []` below would be
    // an empty `practitioners` filter, which the server reads as the whole practice. Once the list has arrived the
    // request is exactly as before, an empty list included.
    if (isNurse && !assignedProviderIds) return null;
    return {
      organizationId: user.organizationId,
      practiceId: effectivePracticeId,
      ...(isNurse
        ? { practitionerIds: assignedProviderIds ?? [] }
        : { practitionerId: selfId }),
      timezone: user.timeZone,
      // providers / room are single-day splits → query a day window. week/month
      // query their own window; day is a day.
      range: rangeFor(view === 'week' ? 'week' : view === 'month' ? 'month' : 'day', date),
      // Cancelled and NoShow visits stay on the calendar (PM sends `false`; PROD-3313).
      showActiveAppointmentOnly: false,
      ...(monthServerFilters ?? {}),
    };
  }, [
    monthServerFilters,
    user?.organizationId,
    effectivePracticeId,
    selfId,
    user?.timeZone,
    isNurse,
    assignedProviderIds,
    view,
    date,
  ]);

  const { data, isLoading: isAppointmentsLoading, isError: isAppointmentsError, isFetching } = useAppointments(queryParams);
  // The nurse's request is held until her list arrives (above): that wait is loading too, not an empty calendar.
  const isLoading = isAppointmentsLoading || (isNurse && !assignedProviderIds);
  const [dayScrollRequest, setDayScrollRequest] = useState(0);

  // Realtime (PROD-1715) — surgical merge of UpdateAppointments into the React
  // Query cache, plus calendar-slots invalidation on RefreshSlots. Fully ported
  // (`use-scheduling-realtime.ts`) but never mounted — PA's calendar had no live
  // appointment/slot updates. Mirrors PM `scheduling-page.tsx`. Vitals realtime
  // (`use-vitals-realtime.ts`) stays an explicit no-op stub — encounter-module
  // scope, out of bounds for this ticket.
  useSchedulingRealtime({
    appointmentsParams: queryParams,
    selectedDateISO: date,
    practiceId: effectivePracticeId ?? null,
    calendarView: dateFilterToCalendarView(viewModeToDateFilter(view)),
  });

  // Flat appointment items for the whole fetched range. The MONTH response is
  // grouped as `appointmentByGroupBy[]` (not a flat `appointments` array), so
  // flatten it — otherwise month-event clicks can't resolve the appointment
  // (handleAppointmentClick would find nothing and fall back to navigate).
  const appointments = useMemo(() => {
    const grouped = (
      data as { appointmentByGroupBy?: { appointmentList?: unknown[] }[] } | undefined
    )?.appointmentByGroupBy;
    if (grouped && grouped.length > 0) {
      return grouped.flatMap((g) => g.appointmentList ?? []) as ListAppointmentItem[];
    }
    return data?.appointments ?? [];
  }, [data]);

  // ── Provider strip slot enrichment (PROD-1190) ──────────────────────────
  // The in-scope provider ids for the strip — same role rule as the grid:
  // physician → self; nurse → assigned providers. Sorted + memoized so the
  // calendarSlots queryKey is bit-identical across re-renders.
  const inScopeProviderIds = useMemo<string[]>(() => {
    const ids = isNurse ? assignedProviderIds ?? [] : selfId ? [selfId] : [];
    return [...ids].sort();
  }, [isNurse, assignedProviderIds, selfId]);

  // calendarSlots feed — the rich strip source (workingHours / nextSlots /
  // appointmentProfile quick-book chips). Scoped to the selected DAY and the
  // in-scope providers, mirroring PM's request (org + practice + day window +
  // practitioners). Enabled only once practice + scope are known (the hook
  // also gates on practiceId). Day-scoped on purpose: the strip always
  // reflects the selected day's availability, even in week/month views.
  const calendarSlotsParams = useMemo(() => {
    if (!user?.organizationId || !effectivePracticeId) return null;
    if (inScopeProviderIds.length === 0) return null;
    return {
      organizationId: user.organizationId,
      practiceId: effectivePracticeId,
      startDate: date,
      endDate: date,
      practitioners: inScopeProviderIds,
    };
  }, [user?.organizationId, effectivePracticeId, date, inScopeProviderIds]);
  const {
    data: slotsResponse,
    isError: isSlotsError,
    isLoading: isSlotsLoading,
    refetch: refetchSlots,
  } = useCalendarSlots(calendarSlotsParams);

  const selectedDateObj = useMemo(() => parseIso(date), [date]);

  // Exam rooms → the By-Room column set (fetched only for the Room view).
  const { data: roomColumns = [], isLoading: roomsLoading } = useExamRooms(
    view === 'room' ? effectivePracticeId : null,
  );

  // ── Toolbar / sub-toolbar supporting data ───────────────────────────────
  const { data: orgLocations } = useOrgLocations(user?.organizationId);
  const statusOptions = useMemo(
    () =>
      (statusList ?? [])
        .filter((s): s is typeof s & { code: string } => !!s.code)
        .map((s) => ({ code: s.code, display: s.display ?? s.code })),
    [statusList],
  );

  // Specialty filter — derived from the day's providers (the toolbar chips).
  // Empty selection = "All". Persisted selection lives in the scheduling-store.
  const selectedSpecialties = useSchedulingStore((s) => s.selectedSpecialties);
  const toggleSpecialty = useSchedulingStore((s) => s.toggleSpecialty);
  const clearSpecialties = useSchedulingStore((s) => s.clearSpecialties);
  const selectedProviderIds = useSchedulingStore((s) => s.selectedProviderIds);
  const setSelectedProviders = useSchedulingStore((s) => s.setSelectedProviders);
  const toggleProvider = useSchedulingStore((s) => s.toggleProvider);
  const clearProviders = useSchedulingStore((s) => s.clearProviders);

  // Specialty chips. Mirror PM's source hierarchy (scheduling-page.tsx
  // ~409-417): calendarSlots is the PRIMARY source — it carries each provider's
  // full capability roster (`speciality` + `doctorSpecialties[]`), so the chip
  // row shows every specialty a provider CAN offer, not only those with an
  // appointment booked today. (That undercount — deriving chips from the day's
  // appointments alone — is the parity gap Tariq caught.) Fall back to the
  // appointment-derived set only when calendarSlots is empty/errored.
  const specialtyLabels = useMemo(() => {
    if (!isSlotsError && slotsResponse && slotsResponse.length > 0) {
      const fromSlots = specialtiesFromCalendarSlots(slotsResponse);
      if (fromSlots.length > 1) return fromSlots;
    }
    const labels = new Set<string>();
    for (const a of appointments) {
      const prov = a.participant?.find((p) => p.provider)?.provider;
      // `speciality` ships on the wire but isn't on PA's lean ProviderResponse
      // type — read it loosely (specialityDisplay accepts `unknown`).
      const label = specialityDisplay((prov as { speciality?: unknown } | undefined)?.speciality);
      if (label) labels.add(label);
    }
    return ['All', ...Array.from(labels).sort()];
  }, [slotsResponse, isSlotsError, appointments]);

  // Doctor columns: nurse → assigned providers; physician → just self. Each is
  // an {id, name} pair; the name falls back to the appointment participant.
  const doctors = useMemo<{ id: string; name: string }[]>(() => {
    if (isNurse) {
      const ids = assignedProviderIds ?? [];
      return ids.map((id) => {
        const prov = appointments
          .map((a) => a.participant?.find((p) => p.provider)?.provider)
          .find((p) => p?.id === id);
        return { id, name: prov?.name?.trim() || 'Provider' };
      });
    }
    if (!selfId) return [];
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Me';
    return [{ id: selfId, name }];
  }, [isNurse, assignedProviderIds, appointments, selfId, user?.firstName, user?.lastName]);

  // Provider summaries for the ProviderStrip. Mirrors PM's source hierarchy
  // (scheduling-page.tsx ~363-372): the calendarSlots feed is the rich,
  // primary source — it carries name/photo/specialty PLUS workingHours, the
  // day's slot list (slotsToday + next-slots), and appointmentProfile
  // quick-book chips, all built by the ported `providersFromCalendarSlots`
  // mapper. We narrow it to the in-scope providers (physician self / nurse
  // assigned). When calendarSlots is empty or errored, fall back to the
  // minimal appointment-derived shape so the strip still renders the day's
  // doctors (counts only, empty rails) rather than vanishing (PROD-1190).
  const providerSummaries = useMemo<CalendarProviderSummary[]>(() => {
    const useSlots = !isSlotsError && slotsResponse && slotsResponse.length > 0;
    if (useSlots) {
      const inScope = new Set(inScopeProviderIds);
      const fromSlots = providersFromCalendarSlots(slotsResponse, selectedDateObj)
        // Narrow to the role-scoped providers — calendarSlots may return the
        // wider practice roster; the strip shows only who's in scope.
        .filter((p) => inScope.size === 0 || inScope.has(p.id));
      if (fromSlots.length > 0) return fromSlots;
    }
    // Fallback — appointment-derived minimal shape (no slot data available).
    const countById = new Map<string, number>();
    for (const a of appointments) {
      const id = a.participant?.find((p) => p.provider)?.provider?.id;
      if (id) countById.set(id, (countById.get(id) ?? 0) + 1);
    }
    return doctors.map((d) => {
      const prov = appointments
        .map((a) => a.participant?.find((p) => p.provider)?.provider)
        .find((p) => p?.id === d.id);
      return {
        id: d.id,
        name: d.name,
        specialty: specialityDisplay((prov as { speciality?: unknown } | undefined)?.speciality) ?? '',
        initials: initialsOf(d.name),
        photo: prov?.photo ?? undefined,
        available: true,
        slotsToday: countById.get(d.id) ?? 0,
        schedule: '',
        nextSlots: [],
        quickBookGroups: [],
        // The feed failed: the chip and the card say so and offer Retry (PM `calendarSlotsState`), rather than read
        // this fallback's appointment count as free slots.
        ...(isSlotsError ? { slotsState: 'error' as const } : {}),
      };
    });
  }, [
    slotsResponse,
    isSlotsError,
    inScopeProviderIds,
    selectedDateObj,
    doctors,
    appointments,
  ]);

  const allProviderIds = useMemo(() => providerSummaries.map((p) => p.id), [providerSummaries]);

  // The practitioners the specialty chips leave visible (PM `visiblePractitionerIds` / `providers`): they draw the
  // strip and the By doctor columns, and the menu's Select all / "N of M" count them. calendarSlots carries each
  // provider's full specialty roster (primary + `doctorSpecialities`); the appointment-derived fallback has one label.
  const visibleProviderIds = useMemo(() => {
    if (selectedSpecialties.length === 0) return allProviderIds;
    const useSlots = !isSlotsError && slotsResponse && slotsResponse.length > 0;
    if (useSlots) {
      const matching = new Set(
        slotsResponse
          .filter((p) => providerMatchesSpecialties(p, selectedSpecialties))
          .map((p) => p.id)
          .filter((id): id is string => Boolean(id)),
      );
      return allProviderIds.filter((id) => matching.has(id));
    }
    return providerSummaries.filter((p) => selectedSpecialties.includes(p.specialty)).map((p) => p.id);
  }, [selectedSpecialties, allProviderIds, isSlotsError, slotsResponse, providerSummaries]);
  const visibleProviders = useMemo(
    () => providerSummaries.filter((p) => visibleProviderIds.includes(p.id)),
    [providerSummaries, visibleProviderIds],
  );
  const allProvidersSelected =
    visibleProviderIds.length > 0 && visibleProviderIds.every((id) => selectedProviderIds.includes(id));
  const noProvidersSelected = selectedProviderIds.length === 0;

  // Flutter/PM parity (PM scheduling-page.tsx ~374-392; Flutter
  // calendar_tab.dart:3420-3425): on first load with no persisted selection,
  // default to ALL providers selected — so every ProviderCard renders its
  // check FILLED (and the grid is unfiltered) rather than every card unchecked
  // while the grid still shows every appointment. The ref scopes this to one
  // fire per mount, so an explicit "unselect all" within the session is
  // respected; the next remount re-fills. (This is the empty-check delta.)
  const didAutoFillProvidersRef = useRef(false);
  useEffect(() => {
    if (didAutoFillProvidersRef.current) return;
    if (allProviderIds.length === 0) return;
    didAutoFillProvidersRef.current = true;
    if (useSchedulingStore.getState().selectedProviderIds.length > 0) return;
    setSelectedProviders(allProviderIds);
  }, [allProviderIds, setSelectedProviders]);


  // ── Client-side filter pipeline (PROD-3307 C2; PM `scheduling-page.tsx` `filteredAppointments`) ──────────────
  // The request fires once per date and scope and never narrows for filters; every filter applies here, so a chip,
  // a status, a location or an invoice status moves the grid at once. Stages pass the response through when their
  // filter is empty, except the provider stage: an explicit "Unselect all" (empty selection) hides every appointment
  // (PM, Andrew 2026-05-25). The first-load auto-fill above selects every practitioner, so empty means "Unselect all".
  //   1. practitioner (specialty ∩ checkbox)  2. status (empty = all but Cancelled)  3. location  4. invoice status
  // Month keeps the unfiltered response: its rows are capped per day by the server, so a client filter over them
  // would be lossy (PM sends Month's filters server-side, a separate query: C6 / D21).
  const { selectedStatusCodes, selectedLocationIds, selectedInvoiceStatuses } = subToolbarFilters;
  const effectiveProviderIds = useMemo(() => {
    if (selectedProviderIds.length === 0) return [];
    const set = new Set(selectedProviderIds);
    return visibleProviderIds.filter((id) => set.has(id));
  }, [visibleProviderIds, selectedProviderIds]);
  const filteredResponse = useMemo(() => {
    if (!data) return undefined;
    let stage = { ...data, appointments } as typeof data;
    // Only once the practitioners are known: with no practitioner list at all there is nothing to select, and the
    // stage would hide what the request returned (that is scope, which this pipeline does not change).
    if (allProviderIds.length > 0) {
      const set = new Set(effectiveProviderIds);
      stage = {
        ...stage,
        appointments: (stage.appointments ?? []).filter((a) => appointmentProviderIds(a).some((id) => set.has(id))),
      };
    }
    stage = filterAppointmentsByStatus(stage, selectedStatusCodes) ?? stage;
    stage = filterAppointmentsByLocation(stage, selectedLocationIds) ?? stage;
    return filterAppointmentsByInvoiceStatus(stage, selectedInvoiceStatuses);
  }, [
    data,
    appointments,
    allProviderIds,
    effectiveProviderIds,
    selectedStatusCodes,
    selectedLocationIds,
    selectedInvoiceStatuses,
  ]);

  // ── Custom-grid data (PROD-1190) ─────────────────────────────────────────
  // The ported PM grids consume `DayListRow[]` (day / doctor / room) and
  // `CalendarAppointment[]` (week) — both derived from the SAME fetched
  // `AppointmentsDetailsResponse` via the full calendar-mapping.

  // Practice working hours → the visible hour gutter (so early-morning /
  // after-hours bookings don't clip). 5-min cached; safe to fetch always.
  const { data: practiceWorkingHours } = usePracticeWorkingHours(effectivePracticeId);

  // Map from the unified flat `appointments` (already flattens the grouped
  // MONTH response above) so day/week/month all feed the mapper a flat list —
  // otherwise the month grid is empty (the month API returns
  // `appointmentByGroupBy[]`, not `appointments`). Single source of truth.
  // Day / By doctor / By room / Week read the filtered response. `allRows` maps every fetched appointment: the
  // click routing looks a tile up in it whatever the view and the filters.
  const dayListRows = useMemo<DayListRow[]>(() => mapAppointmentsToDayList(filteredResponse), [filteredResponse]);
  const allRows = useMemo<DayListRow[]>(
    () => mapAppointmentsToDayList({ ...data, appointments } as unknown as typeof data),
    [data, appointments],
  );
  const dayListHours = useMemo<number[]>(
    () => hoursForDayList(dayListRows, practiceWorkingHours, selectedDateObj),
    [dayListRows, practiceWorkingHours, selectedDateObj],
  );
  const dayListCurrentHour = useMemo(() => {
    const now = practiceNow();
    return toIso(now) === date ? now.getHours() : undefined;
  }, [date]);

  // The Week, By doctor and By room grids open on now, not on their first hour (design log 15 Oct). The DS grids
  // are 80 px (Week) and 60 px (By doctor, By room) an hour. Above every early return, like every hook here.
  const gridRef = useRef<HTMLDivElement>(null);
  useScrollToNow(gridRef, {
    enabled: view === 'week' || view === 'providers' || view === 'room',
    startHour: dayListHours[0] ?? DS_DEFAULT_GRID_START_HOUR,
    endHour: (dayListHours[dayListHours.length - 1] ?? DS_DEFAULT_GRID_END_HOUR - 1) + 1,
    hourHeight: view === 'week' ? DS_WEEK_HOUR_HEIGHT : DS_DOCTOR_ROOM_HOUR_HEIGHT,
    key: `${view}|${date}`,
  });

  // Week only: which column (1-based, after the gutter) holds today, for the bridge's column tint.
  const todayColumn = useMemo(() => {
    if (view !== 'week' || !showsToday(selectedDateObj, 'Week')) return undefined;
    return ((practiceNow().getDay() + 6) % 7) + 2;
  }, [view, selectedDateObj]);

  // Week tiles use the week window; day tiles use the day. (mapAppointments*
  // reads the same response; pick by view to keep the tile dayIndex correct.)
  const weekStartISO = useMemo(() => rangeFor('week', date).startDate.slice(0, 10), [date]);
  const weekTiles = useMemo(
    () => mapAppointmentsToWeekTiles(filteredResponse, `${weekStartISO}T00:00:00`, 7),
    [filteredResponse, weekStartISO],
  );

  // The doctor columns shown in the By-Doctor grid — the provider strip's
  // selection narrows them (empty selection = all in-scope providers), mirror
  // of PM frame-scheduling.
  const doctorColumns = useMemo<ProviderSummary[]>(
    () =>
      selectedProviderIds.length > 0
        ? visibleProviders.filter((p) => selectedProviderIds.includes(p.id))
        : visibleProviders,
    [visibleProviders, selectedProviderIds],
  );

  // Group the day's rows by their provider / room for the split grids. Empty
  // doctor/room columns still render (the grids seed every column key).
  const rowsByDoctor = useMemo(
    () => groupRowsByDoctor(dayListRows),
    [dayListRows],
  );
  const rowsByRoom = useMemo(() => groupRowsByRoom(dayListRows), [dayListRows]);

  // Month grid — the Practice calendar's derivation (`lib/month-day-data.ts`, PM `scheduling-page.tsx` `monthGrid`):
  // the server's day groups (≤ 3 rows each, "+N more" = the raw `group.count`) with the blocks merged in and sorted,
  // Cancelled hidden unless a status is chosen; then the DS 6 × 7 grid builder.
  const monthGrid = useMemo(
    () =>
      buildMonthGrid({
        monthAnchor: selectedDateObj,
        selectedISO: toLocalISO(selectedDateObj),
        dayData: monthDayData(view === 'month' ? data : undefined, selectedStatusCodes),
        todayISO: toLocalISO(practiceNow()),
      }),
    [view, data, selectedStatusCodes, selectedDateObj],
  );

  const monthLabel = useMemo(
    () => formatMonthLabel(selectedDateObj),
    [selectedDateObj],
  );

  // Week-grid day headers (Mon–Sun) for the WeekCalendar.
  const weekDays = useMemo<PACalendarDayColumn[]>(() => {
    const start = parseIso(weekStartISO);
    const todayStr = toIso(practiceNow());
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const iso = toIso(d);
      return {
        dayOfWeek: labels[i],
        date: d.getDate(),
        isToday: iso === todayStr,
        dateISO: iso,
      };
    });
  }, [weekStartISO]);
  // A week change slides the new week in from the side moved towards (PM `week-calendar.tsx` `cal-swap-next / -prev`,
  // 16 px, 220 ms; reduced motion swaps). Derived while rendering from the previous week start.
  const [weekSwap, setWeekSwap] = useState<{ key: string; dir: '' | 'next' | 'prev' }>({ key: weekStartISO, dir: '' });
  if (weekSwap.key !== weekStartISO) {
    setWeekSwap({ key: weekStartISO, dir: weekStartISO > weekSwap.key ? 'next' : 'prev' });
  }

  // ── Permissions (fail-closed) ───────────────────────────────────────────
  //
  // The Create-menu gates mirror the PA FLUTTER app one-for-one. Andrew's
  // ruling (PROD-1817): PA React's permission model follows PA Flutter, NOT PM.
  // `calendar_screen.dart:2607-2662` `setCreateMenuOptions()`:
  //
  //   validatePermission([Manage_Patient_Appointments], …, add: true) → "New Appointment"
  //   validatePermission([Doctor_Calender],             …, add: true) → "Block Availability"
  //
  // The PA calendar offers only the first since 21 Oct (supervisor ruling: no block availability), so only
  // `Manage_Patient_Appointments` add is read here. Same keys, same `add` flag. Confirmed live against
  // dev-provider as Nader Hebela (`Doctor_Calender {add:false, view:true}`):
  // Flutter offers New Appointment only, and so does this.
  //
  // DO NOT flatten these to `true` to match PM. PM hardcodes both
  // (frame-scheduling.tsx:872-873) because it is practice management, where
  // staff legitimately manage every calendar; `Doctor_Calender` governs OTHER
  // doctors' calendars, so copying PM grants a practitioner an action Flutter
  // withholds. That change was made once (dc16039) and reverted (dd92cc8)
  // after review flagged it as a possible privilege escalation.
  const perms = user?.systemPermissions;
  const canCreateAppointment = evaluatePermissions(perms, ['Manage_Patient_Appointments'], { add: true });
  const showBillingSummary = evaluatePermissions(perms, ['Show_Billing_Summary'], { view: true });

  // ── Panels (Create menu) ────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  // Quick-create pre-fill (PROD-1190 follow-up): when the user clicks a slot or
  // an appointment-profile chip on a provider card, open the Create panel
  // pre-filled with that provider + profile (+ slot). Was unwired — the card's
  // onSelectSlot/onSelectProfile never reached the page, so clicks did nothing.
  const [pendingInitialValues, setPendingInitialValues] =
    useState<Partial<AppointmentFormValues> | null>(null);

  // ── View-appointment popup ──────────────────────────────────────────────
  const [popupTarget, setPopupTarget] = useState<ViewAppointmentTarget | null>(null);
  // ── Inline edit / reschedule rail (PROD-3307 C8; PM `openEditPanel`) ───────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<CalendarEditTarget | null>(null);
  // New appointment taking the right side closes the edit rail (one rail at a time).
  useEffect(() => {
    if (createOpen) setEditTarget(null);
  }, [createOpen]);

  // ── Row-action dialogs (PROD-1193) ───────────────────────────────────────
  // Dedicated quick-dialogs opened straight from the ⋯ row menu (no longer a
  // bounce through the detail popup). Mirrors PM frame-scheduling.tsx — each
  // target is the clicked DayListRow, which already carries every id the
  // dialogs/hooks need (id, start/end, patientId, practitionerId, recurringId,
  // appointmentProfileId, note).

  // ── Drag-drop reschedule (PROD-1714a) ───────────────────────────────────
  // `drop-routing.ts` was fully ported but had 0 importers — the Week/By-Doctor
  // grids already accept `onAppointmentDrop`/`onByDoctorAppointmentDrop`, they
  // just weren't wired. Mirrors PM `scheduling-page.tsx` exactly: a Week drop fires
  // the reschedule mutation directly; a By-Doctor drop confirms first (Flutter
  // `calendar_by_doctor_widget.dart:910-927`). Recurring or not, a drag moves the
  // ONE occurrence (PM, Flutter `QuickRescheduleAppointmentEvent`): no scope dialog,
  // so Following / All can no longer move a whole series (Andrew's review, PROD-3313).
  const reschedule = useRescheduleAppointment();
  const [confirmDrop, setConfirmDrop] = useState<{
    id: string;
    newStartISO: string;
    newEndISO: string;
    oldStartISO: string | null;
    providerName: string | null;
    patientName: string | null;
  } | null>(null);

  const fireRescheduleThisOnly = (id: string, newStartISO: string, newEndISO: string) => {
    reschedule.mutate(
      { appointmentId: id, startDate: newStartISO, endDate: newEndISO },
      {
        onSuccess: () => toast.success('Appointment rescheduled'),
        onError: () => toast.error("Couldn't reschedule — try again."),
      },
    );
  };

  // ── Tenant guard (review follow-up) ─────────────────────────────────────
  // Both drop handlers below resolve the dragged tile's `id` ONLY against
  // `appointments` — never against the raw drop payload directly. `appointments`
  // is derived from `useAppointments(queryParams)`, and `queryParams` is null
  // unless `effectivePracticeId` (the practice locked via the effect above,
  // PROD-1478) is resolved and is baked into the request body the backend
  // scopes the response to (mirrors every other query/mutation in this file —
  // see `effectivePracticeId` threaded through `calendarSlotsParams`,
  // `assignedParams`, etc.). A drop can therefore never resolve to a
  // cross-tenant appointment: an id absent from this practice's fetched list
  // routes to `routeAppointmentDrop`'s `'error'` branch (toasted, no mutation
  // fires), it never falls through to a "trust the client" mutation. The
  // `effectivePracticeId` check here is the same explicit guard style used
  // throughout this file (`if (!effectivePracticeId) return null`) — belt-
  // and-suspenders on top of that structural guarantee, and on top of the
  // server-side enforcement (api-client's `OrganizationId`/`PracticeId`
  // headers are set STRICTLY from the authenticated session, never from
  // request-body content — shared/api/api-client.ts `buildHeaders`).
  function handleAppointmentDrop({ id, newStartISO }: { id: string; newStartISO: string; dayIndex: number }) {
    if (!effectivePracticeId) return;
    const route = routeAppointmentDrop({
      dayAppointments: { appointments } as unknown as Parameters<
        typeof routeAppointmentDrop
      >[0]['dayAppointments'],
      event: { id, newStartISO },
    });
    if (route.kind === 'error') {
      toast.error(route.message);
      return;
    }
    fireRescheduleThisOnly(route.payload.id, route.payload.newStartISO, route.payload.newEndISO);
  }

  // Same tenant guard as `handleAppointmentDrop` above — see that comment.
  function handleByDoctorDrop({ id, newStartISO }: { id: string; newStartISO: string }) {
    if (!effectivePracticeId) return;
    // A new drop while the confirm is open is ignored (PM `handleByDoctorDrop`).
    if (confirmDrop) {
      toast.message('Finish the open reschedule first.');
      return;
    }
    const route = routeAppointmentDrop({
      dayAppointments: { appointments } as unknown as Parameters<
        typeof routeAppointmentDrop
      >[0]['dayAppointments'],
      event: { id, newStartISO },
    });
    if (route.kind === 'error') {
      toast.error(route.message);
      return;
    }
    // Recurring or not, the confirm moves the one occurrence (PM).
    const apt = appointments.find((a) => a.id === id);
    const part = apt?.participant;
    const prov = part?.find((p) => (p.referenceType ?? '').toLowerCase() === 'provider')?.provider;
    const pat = findPatientParticipant(part)?.patient;
    setConfirmDrop({
      id: route.payload.id,
      newStartISO: route.payload.newStartISO,
      newEndISO: route.payload.newEndISO,
      oldStartISO: apt?.start ?? null,
      providerName: prov?.name ?? null,
      patientName: [pat?.firstName, pat?.lastName].filter(Boolean).join(' ') || null,
    });
  }

  const [cancelTarget, setCancelTarget] = useState<DayListRow | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<DayListRow | null>(null);
  const [noteTarget, setNoteTarget] = useState<DayListRow | null>(null);
  // Row whose "View PVS" menu item was clicked (PROD-1651). Opens the shared
  // PostVisitSummaryDialog. PA had the action id and `shouldShowPvsMenuItem`
  // but never wired either — the menu item, this state and the dialog were all
  // missing, so the row menu was one item shorter than PM's.
  const [pvsTarget, setPvsTarget] = useState<DayListRow | null>(null);

  // Carry the encounter nav context (patient/demographics/specialtyId/encounterId +
  // appointmentStart/End) on calendar→encounter navigations, mirroring the Today's-Appointments
  // entry — so the rail's Reschedule (PROD-1246) shows and the template axes/fast-path resolve
  // without a GET-by-appointment. undefined when the row isn't loaded → page falls back. PROD-1254.
  const navStateFor = (appointmentId: string) => {
    const state = encounterNavStateFor(appointments, appointmentId);
    return state ? { state } : undefined;
  };

  // The ported grids report a clicked appointment by id. Derive the popup
  // target from the underlying appointment item; the popup re-fetches full
  // details (gated on the ids present) and exposes the PA "Open encounter"
  // action. If the row is missing the ids, fall back to the direct navigate so
  // the click is never a dead end (kept from the DS-grid path).
  function handleAppointmentClick(appointmentId: string) {
    // `allRows` maps every fetched appointment, so the block lookup works from any view.
    const clickedRow = allRows.find((r) => r.id === appointmentId);
    // A block draws read-only: the PA calendar has no block availability (supervisor ruling, 21 Oct), so a block
    // tile or row opens nothing.
    if (clickedRow?.isBlock) return;
    const item = appointments.find((a) => a.id === appointmentId) as
      | (ListAppointmentItem & {
          appointmentProfile?: { id?: string; referenceId?: string };
        })
      | undefined;
    const patientId = findPatientParticipant(item?.participant)?.patient?.id;
    const providerId = item?.participant?.find((p) => p.provider)?.provider?.id;
    // PreLoadAppointmentDetails keys off the profile's `referenceId`, NOT its
    // instance `id` (Flutter `appointment_popup_card.dart:104`). Sending `id`
    // returns an empty 200 → the popup showed no details. Fall back to `id`
    // only if referenceId is absent. PROD-1190 fix.
    const appointmentProfileId =
      item?.appointmentProfile?.referenceId ?? item?.appointmentProfile?.id;
    if (item && patientId && providerId && appointmentProfileId) {
      setPopupTarget({
        appointmentId,
        patientId,
        providerId,
        appointmentProfileId,
        // Seed the popup from the already-loaded calendar appointment so its
        // core fields show even when PreLoad answers 200-empty (Flutter parity).
        seedAppointment: item as unknown as AppointmentResponse,
      });
      return;
    }
    void navigate(`/encounter/${appointmentId}`, navStateFor(appointmentId));
  }

  // Row-action affordances on the day-list event cells. cancel / reschedule /
  // add-note open their dedicated dialogs straight from the ⋯ menu (PROD-1193,
  // mirroring PM frame-scheduling.tsx); edit / view-recurring-series /
  // open-billing-tab + anything else route through the appointment view (the
  // popup) as before. Status transitions are handled in-row by the grid.
  /**
   * Canonical view-appointment URL for a day-list row — PM's
   * `buildViewAppointmentUrl` (frame-scheduling.tsx:562-576) with PA's calendar
   * route in the `from` param so the panel's Back lands on this exact day/view.
   */
  function buildViewAppointmentUrl(row: DayListRow, tab?: string, completePatient = false): string {
    const params = new URLSearchParams();
    if (row.patientId) params.set('patientId', row.patientId);
    if (row.practitionerId) params.set('providerId', row.practitionerId);
    if (row.appointmentProfileId) params.set('profileId', row.appointmentProfileId);
    if (tab && tab !== 'patient') params.set('tab', tab);
    if (completePatient) params.set('complete', 'patient');
    params.set('from', `/calendar?date=${date}&view=${viewModeToDateFilter(view)}`);
    const qs = params.toString();
    return `/scheduling/appointment/${row.id}${qs ? `?${qs}` : ''}`;
  }

  function handleRowAction(row: DayListRow, action: AppointmentActionId) {
    switch (action) {
      case 'cancel':
        setCancelTarget(row);
        return;
      case 'reschedule': {
        // Recurring OR not → the rail's Reschedule state for this one occurrence (PM `frame-scheduling.tsx`
        // 'reschedule' → `openEditPanel({ mode: 'reschedule' })`, Flutter `calendar_tab.dart:2242-2253`: no scope
        // picker; the popup's Reschedule already did this). The series is rescheduled from the series menu, as in PM
        // (Andrew's review, PROD-3313). The date/time dialog only when the visit lacks the rail's ids.
        const target = editTargetFor(row.id);
        if (target) {
          openEditRail({ ...target, startIn: 'reschedule' });
          return;
        }
        setRescheduleTarget(row);
        return;
      }
      case 'add-note':
        setNoteTarget(row);
        return;
      case 'open-encounter':
        // Flutter `calendar_tab.dart:2609-2612` navigates to the encounter for
        // the row's appointment. `navStateFor` carries the same context the
        // popup's "Open encounter" passes, so the encounter page does not have
        // to re-fetch by appointment id.
        void navigate(`/encounter/${row.id}`, navStateFor(row.id));
        return;
      case 'view-pvs':
        // Flutter `calendar_tab.dart:2638-2639` calls openPvs(id); React opens
        // the shared Post-Visit Summary dialog for the row's appointment.
        setPvsTarget(row);
        return;
      case 'edit':
        // PM frame-scheduling.tsx:645 navigates straight to the edit panel;
        // PA was falling through to the popup, so "Edit Appointment" needed a
        // second click on the pencil to actually reach the editor.
        // Flutter does the same (calendar_tab.dart:2585-2594 -> onSelect ->
        // the edit screen).
        void navigate(buildViewAppointmentUrl(row));
        return;
      case 'open-billing-tab':
        void navigate(buildViewAppointmentUrl(row, 'billing'));
        return;
      case 'view-recurring-series':
        void navigate(buildViewAppointmentUrl(row, 'appointments'));
        return;
      case 'follow-up':
        // The Create panel pre-filled from the visit (PM `frame-scheduling.tsx` 'follow-up', Flutter
        // `calendar_tab.dart:2306-2307`), its profile picker locked to follow-up profiles by `referenceAppointmentId`.
        openFollowUp(buildFollowUpInitialFromRow(row, firstOrgLocation(), followUpSeedDateISO(selectedDateObj, new Date())));
        return;
      default:
        // anything else still routes through the popup.
        handleAppointmentClick(row.id);
        return;
    }
  }

  /** The user's first organisation location with a real id (Flutter `calendar_screen.dart:180`), else null. */
  function firstOrgLocation() {
    const first = user?.organizationLocations?.[0];
    return first?.id ? first : null;
  }

  // A follow-up opens the Create panel pre-filled. The panel is the Create menu's "New Appointment", so it keeps that
  // item's permission (PROD-1817, `Manage_Patient_Appointments` add): never a create the menu would not offer.
  function openFollowUp(initial: Partial<AppointmentFormValues>) {
    if (!canCreateAppointment) {
      toast.error('You are not authorized to book appointments.');
      return;
    }
    setPendingInitialValues(initial);
    setCreateOpen(true);
  }

  // By doctor: a double-click on an empty slot books that practitioner there with his preferred-or-first profile
  // (PM `frame-scheduling.tsx` `handleGridQuickCreate`; the DS grid's `onSlotDoubleClick`). No profile → nothing.
  function handleGridQuickCreate(doctorId: string, startISO: string) {
    if (!canCreateAppointment) return;
    const provider = providerSummaries.find((p) => p.id === doctorId);
    const profile = provider?.preferredProfile;
    if (!profile) return;
    const endISO = addMinutesISO(startISO, profile.minutesDuration ?? 0);
    handleQuickCreate(doctorId, profile, { start: startISO, end: endISO } as SlotResponse);
  }

  /** The rail's four ids from a calendar appointment (the popup's own derivation), or null when one is missing. */
  function editTargetFor(appointmentId: string): Omit<CalendarEditTarget, 'startIn'> | null {
    const item = appointments.find((a) => a.id === appointmentId) as
      | (ListAppointmentItem & { appointmentProfile?: { id?: string; referenceId?: string } })
      | undefined;
    const patientId = findPatientParticipant(item?.participant)?.patient?.id;
    const providerId = item?.participant?.find((p) => p.provider)?.provider?.id;
    const appointmentProfileId = item?.appointmentProfile?.referenceId ?? item?.appointmentProfile?.id;
    if (!item || !patientId || !providerId || !appointmentProfileId) return null;
    return { appointmentId, patientId, providerId, appointmentProfileId };
  }

  // One rail at a time on the right: opening the edit rail closes New appointment and the Block panel.
  function openEditRail(target: CalendarEditTarget) {
    setPopupTarget(null);
    setCreateOpen(false);
    setPendingInitialValues(null);
    setEditTarget(target);
  }

  function handleEnterEncounter(appointmentId: string) {
    setPopupTarget(null);
    void navigate(`/encounter/${appointmentId}`, navStateFor(appointmentId));
  }

  // Pencil (edit) — PROD-1712. The popup was passed no `onEdit`, so the pencil
  // rendered disabled ("Edit screen — PROD-377"). All the edit BUILDING BLOCKS
  // already existed (CreateAppointmentPanel mode="edit" + editAppointment,
  // appointment-to-form-values.ts, form-edit-overlay.ts buildEditAppointmentBody,
  // useUpdateAppointment) but nothing entered edit mode. Mirrors PM
  // scheduling-page.tsx's onEdit exactly: navigate to the routed deep-view panel
  // (`ViewAppointmentPanelPage`, PROD-1712/1713), carrying `from=` so the panel's
  // Back button restores this exact calendar day/view.
  function handleEditAppointment(tab?: string) {
    if (!popupTarget) return;
    // Edit and Reschedule stay on the calendar, in the rail (PM `scheduling-page.tsx` `onEdit` / `onReschedule` →
    // `openEditPanel`). "Proceed to complete patient" and a named tab (Billing…) still open the full appointment page.
    if (tab === undefined || tab === 'patient' || tab === 'reschedule' || tab === 'cancel') {
      openEditRail({
        appointmentId: popupTarget.appointmentId,
        patientId: popupTarget.patientId,
        providerId: popupTarget.providerId,
        appointmentProfileId: popupTarget.appointmentProfileId,
        ...(tab === 'reschedule' || tab === 'cancel' ? { startIn: tab } : {}),
      });
      return;
    }
    const params = new URLSearchParams({
      patientId: popupTarget.patientId,
      providerId: popupTarget.providerId,
      profileId: popupTarget.appointmentProfileId,
    });
    // 'complete' = "Proceed to complete patient": the page scrolls to what is missing (not a tab).
    if (tab === 'complete') params.set('complete', 'patient');
    else if (tab && tab !== 'patient') params.set('tab', tab);
    params.set('from', `/calendar?date=${date}&view=${viewModeToDateFilter(view)}`);
    void navigate(`/scheduling/appointment/${popupTarget.appointmentId}?${params.toString()}`);
  }

  // Quick-create from a provider card — a slot click (slot present) or an
  // appointment-profile chip (slot null). Builds the create-form pre-fill and
  // opens the panel; the PreBook chain (reminders/billing/reasons) fires from
  // the profile section's hydration effect. Mirrors PM frame-scheduling.tsx
  // `handleQuickCreate` (PROD-829 / Flutter `calendar_screen.dart:307-410`).
  // `locationOnly`: the hover card's Book tiles (In clinic / Video / Home) keep only the location and leave the profile
  // to the user (PM `handleQuickCreate(..., { locationOnly: true })`, the card's `onBookLocation`).
  function handleQuickCreate(
    providerId: string,
    profile: CreateAppointmentProfileRequest,
    slot: SlotResponse | null,
    { locationOnly = false }: { locationOnly?: boolean } = {},
  ) {
    // The provider strip's slot chips and Book tiles open New appointment, so they keep its permission (PROD-1817,
    // `Manage_Patient_Appointments` add), refused with the follow-up's toast (Andrew's review, PROD-3313).
    if (!canCreateAppointment) {
      toast.error('You are not authorized to book appointments.');
      return;
    }
    const provider = providerSummaries.find((p) => p.id === providerId);
    if (!provider) return;
    const { providerSpeciality: overrideDisplay, providerSpecialityCode } =
      applySpecialityOverride(profile, {
        providerSpecialityDisplay: provider.specialty,
      });
    const builtProfile = buildProfileFormValues(profile);
    const profileValues = locationOnly ? { location: builtProfile.location } : builtProfile;
    setPendingInitialValues({
      providerId,
      providerName: provider.name,
      providerSpeciality: overrideDisplay,
      providerSpecialityCode,
      ...profileValues,
      ...(slot
        ? { slotId: `${providerId}-${slot.start}`, slotStart: slot.start, slotEnd: slot.end }
        : { slotId: null, slotStart: null, slotEnd: null }),
      // Land the panel's date strip on the slot's day (or the selected date).
      selectedDate: slot?.start ? slot.start.slice(0, 10) : date,
    } as Partial<AppointmentFormValues>);
    setCreateOpen(true);
  }

  // The one page header: "Calendar" over a count line (the visits in the period, real data) and Create (the one
  // primary action) on the right. The period controls and the view switch sit in the toolbar row below. The practice
  // picker moved to the global top bar (`top-bar-practice.tsx`, the Practice app's placement); the tabs the DS toolbar
  // drew (Calendar only on the Practitioner app) are the title; the appointment search and the specialty filter sit in
  // the toolbar row below. Same controls, same gates, same handlers.
  const showCreate = canCreateAppointment;
  // The header is the Practice calendar's: "Calendar" alone, no count line (PM `calendar-toolbar.tsx` PageHeader has no
  // `meta`; owner ruling 21 Oct, the calendar is PM's in everything, superseding the design count line of PROD-3307 Q3).
  // Create is PM's control with its one item here (PM keeps the menu even when one item is left, Flutter's
  // PopupMenuButton; `calendar-toolbar.tsx`): New Appointment toggles the create rail (`frame-scheduling.tsx`
  // `setActivePanel`), opening it fresh (no pre-fill). No Block Availability (supervisor ruling, 21 Oct).
  const pageHeader = (
    <PageHeader
      title="Calendar"
      actions={
        showCreate ? (
          <CalendarCreateMenu
            canCreateAppointment={canCreateAppointment}
            onCreateClick={() => {
              setPendingInitialValues(null);
              setCreateOpen((open) => !open);
            }}
          />
        ) : null
      }
    />
  );

  // Calendar appointment search (PROD-1714b): `AppointmentSearch` has its own 30-day-lookback query and navigates on a
  // suggestion; it sits in the toolbar row now.
  const appointmentSearch =
    user?.organizationId && effectivePracticeId ? (
      <div className="cal-search">
        <AppointmentSearch
          organizationId={user.organizationId}
          practiceId={effectivePracticeId}
          selectedDate={selectedDateObj}
          providerIds={isNurse && !assignedProviderIds ? null : inScopeProviderIds}
        />
      </div>
    ) : undefined;

  // The practitioner filter in PM's no-Devices shape (PROD-3307 Q2): the specialty chip row under the toolbar with the ≡
  // Select all / Unselect all (PM `calendar-toolbar.tsx` `showSpecialtyFilterRow`); was the "All ▾" menu on the row.
  // Day and Week (Antonin's export, 22 Oct): the same filter as the scope dropdown ("All ▾") in the toolbar centre, so
  // the practitioner strip sits right under the toolbar; Month, Room and Doctor keep the chip row.
  const scopeInToolbar = view === 'day' || view === 'week';
  const scopeDropdown = (
    <CalendarScopeDropdown
      specialties={specialtyLabels}
      selectedSpecialties={selectedSpecialties}
      onSpecialtyToggle={toggleSpecialty}
      onSpecialtyClear={clearSpecialties}
      onSelectAll={() => setSelectedProviders(visibleProviderIds)}
      onUnselectAll={clearProviders}
      allSelected={allProvidersSelected}
      noneSelected={noProvidersSelected}
    />
  );
  const specialtyRow = (
    <CalendarSpecialtyRow
      specialties={specialtyLabels}
      selectedSpecialties={selectedSpecialties}
      onSpecialtyToggle={toggleSpecialty}
      onSpecialtyClear={clearSpecialties}
      onSelectAll={() => setSelectedProviders(visibleProviderIds)}
      onUnselectAll={clearProviders}
      allSelected={allProvidersSelected}
      noneSelected={noProvidersSelected}
    />
  );

  // Provider strip (the doctor cards) in the top panel — shown for the
  // single-day split views (day / providers / room), matching Flutter where
  // the provider rail sits above the day/doctor calendar (PROD-1190 issue #1:
  // the doctor card was missing from the top panel).
  // Show the provider rail in ALL calendar views (PM frame-scheduling.tsx +
  // Flutter both keep it across day/week/month/doctor/room — Flutter just
  // compacts its height for week/month). PROD-1190 issue #1 follow-up: it was
  // wrongly gated to day/providers/room, so it vanished in week/month.
  // While the slot feed loads and no practitioner is known yet, the strip shows its grey loading chips (Practice
  // `frame-scheduling.tsx` keeps the strip mounted with `loading={providersLoading}`).
  const showProviderStrip = providerSummaries.length > 0 || isSlotsLoading;
  // The practitioners as compact chips (one per practitioner, "+N"), the card behind each chip.
  const providerStrip = showProviderStrip ? (
    <CalendarProviderChips
      providers={visibleProviders}
      selectedProviderIds={selectedProviderIds}
      onToggleProvider={toggleProvider}
      onSelectSlot={(providerId, slot, profile) => handleQuickCreate(providerId, profile, slot)}
      // The card's Book tiles (the only profile entry on the PA card): location only, as PM's `onBookLocation`.
      onSelectProfile={(providerId, _group, profile) => handleQuickCreate(providerId, profile, null, { locationOnly: true })}
      organizationId={user?.organizationId}
      renderSlotsPopover={renderSlotsPopover}
      loading={isSlotsLoading}
      onRetrySlots={() => void refetchSlots()}
    />
  ) : null;

  // The toolbar row (period, ‹ Today ›, view switch) is the app's own composition; the filters that shared the DS
  // sub-toolbar with it sit behind the floating pill over the grid (`filterPill`, below).
  const toolbarRow = (
    <CalendarToolbarRow
      period={
        <CalendarPeriodControls
          selectedDate={selectedDateObj}
          onDateChange={(d) => {
            const iso = toIso(d);
            // The same day again ("Today" on today): no new fetch, so ask the Day list to scroll to now once more.
            if (iso === date) setDayScrollRequest((n) => n + 1);
            else setDate(iso);
          }}
          calendarView={viewModeToDateFilter(view)}
          refetching={isFetching && !isLoading}
        />
      }
      center={scopeInToolbar ? scopeDropdown : undefined}
      search={appointmentSearch}
      views={
        <CalendarViewSwitch
          calendarView={viewModeToDateFilter(view)}
          onViewChange={(f) => handleView(dateFilterToViewMode(f))}
        />
      }
    />
  );

  const filterPill = (
    <CalendarFilterPill
      waitingRoomCount={data?.waitingRoom}
      noShowCount={data?.noShow}
      cancelledCount={data?.cancelled}
      statusOptions={statusOptions}
      organizationLocations={orgLocations ?? []}
      {...subToolbarFilters}
    />
  );

  // The New appointment panel docks on the RIGHT of the calendar (the Practice app's, Antonin 15 Oct), pushing the grid
  // left. The DS frame has no right-rail slot, but it renders `overlays` as the last flex child of the page row, so the
  // panel sits there. It returns null when closed; "Open in full view" draws the same form in a centred dialog.
  const createPanel =
    user?.organizationId && effectivePracticeId ? (
      <CreateAppointmentPanel
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPendingInitialValues(null);
        }}
        organizationId={user.organizationId}
        practiceId={effectivePracticeId}
        // createdBy/updatedBy audit id = the doctorSetupId (user.id) ONLY — never the JWT sub.
        // Deliberately NOT falling back to selfId (= practitionerId ?? id): a mis-populated
        // practitionerId has been seen carrying the account id, so the fallback would reintroduce
        // the very bug this fixes (PROD-1457). A logged-in provider with org+practice always has
        // user.id (GetUserDataToken doctorSetupId); '' only if that profile fetch degraded.
        userId={user?.id ?? ''}
        initialValues={pendingInitialValues ?? undefined}
        onSearchPatients={async () => []}
        showBillingSummary={showBillingSummary}
      />
    ) : null;

  // The edit / reschedule rail docks where New appointment does (the frame's `overlays`, the last flex child).
  const editPanel =
    user?.organizationId && effectivePracticeId ? (
      <CalendarEditPanel
        target={editTarget}
        onClose={() => setEditTarget(null)}
        organizationId={user.organizationId}
        practiceId={effectivePracticeId}
        userId={user?.id ?? ''}
        showBillingSummary={showBillingSummary}
        onCompletePatient={(t) => {
          setEditTarget(null);
          const params = new URLSearchParams({
            patientId: t.patientId,
            providerId: t.providerId,
            profileId: t.appointmentProfileId,
            complete: 'patient',
            from: `/calendar?date=${date}&view=${viewModeToDateFilter(view)}`,
          });
          void navigate(`/scheduling/appointment/${t.appointmentId}?${params.toString()}`);
        }}
      />
    ) : null;

  const overlays = (
    <>
      {createPanel}
      {editPanel}
      <ViewAppointmentPopup
        open={!!popupTarget}
        onOpenChange={(next) => {
          if (!next) setPopupTarget(null);
        }}
        target={popupTarget}
        onEdit={handleEditAppointment}
        onEnterEncounter={handleEnterEncounter}
        onFollowUp={(appt) => {
          setPopupTarget(null);
          openFollowUp(
            buildFollowUpInitialFromAppointment(appt, firstOrgLocation(), followUpSeedDateISO(selectedDateObj, new Date())),
          );
        }}
        showBillingSummary={showBillingSummary}
      />

      {/* ── Row-action dialogs (PROD-1193) ── opened straight from the ⋯ menu.
          Each target is the clicked DayListRow; the dialogs/hooks already
          exist (ported with the calendar). Mirrors PM frame-scheduling.tsx. */}
      <CancelAppointmentDialog
        open={cancelTarget !== null}
        onOpenChange={(next) => {
          if (!next) setCancelTarget(null);
        }}
        target={
          cancelTarget
            ? {
                appointmentId: cancelTarget.id,
                patientId: cancelTarget.patientId,
                practitionerId: cancelTarget.practitionerId,
                startISO: cancelTarget.startISO,
                recurringId: cancelTarget.recurringId,
                patientFirstName: cancelTarget.patientName?.split(' ')[0],
              }
            : null
        }
      />

      {/* Reschedule — focused date+time picker (keeps duration), the single-occurrence reschedule; only for a row
          that lacks the rail's ids. */}
      <RescheduleAppointmentDialog
        open={rescheduleTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRescheduleTarget(null);
        }}
        target={
          rescheduleTarget
            ? {
                appointmentId: rescheduleTarget.id,
                startISO: rescheduleTarget.startISO,
                endISO: rescheduleTarget.endISO,
              }
            : null
        }
      />

      {/* Add internal note — single-field dialog on appointment.comment. */}
      <PostVisitSummaryDialog
        open={pvsTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPvsTarget(null);
        }}
        appointmentId={pvsTarget?.id ?? ''}
      />

      <AddNoteDialog
        open={noteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setNoteTarget(null);
        }}
        target={
          noteTarget
            ? { appointmentId: noteTarget.id, initialNote: noteTarget.note }
            : null
        }
      />

      {/* By-Doctor drag-reschedule confirm (PROD-1714a) — Flutter confirms
          every By-Doctor drag (`calendar_by_doctor_widget.dart:910-927`); the
          Week drag has no confirm. Signal DS `ModalYesNo` (no shadcn
          AlertDialog in this repo). */}
      <ModalYesNo
        open={!!confirmDrop}
        title="Reschedule appointment?"
        message={
          confirmDrop
            ? `Move ${
                confirmDrop.patientName
                  ? `${confirmDrop.patientName}'s appointment`
                  : 'this appointment'
              } from ${formatDropTime(confirmDrop.oldStartISO)} to ${formatDropTime(
                confirmDrop.newStartISO,
              )}?${
                confirmDrop.providerName
                  ? ` It stays assigned to ${confirmDrop.providerName} — only the time changes.`
                  : ' Only the time changes — the provider is unchanged.'
              }`
            : ''
        }
        confirmLabel="Reschedule"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (!confirmDrop) return;
          fireRescheduleThisOnly(confirmDrop.id, confirmDrop.newStartISO, confirmDrop.newEndISO);
          setConfirmDrop(null);
        }}
        onCancel={() => setConfirmDrop(null)}
      />
    </>
  );

  // ── The grid (PROD-1190) — ported PM custom renderers, one per view. ──────
  // While appointments load, PACalendarPage swaps the grid for its brand loader — except Room,
  // which draws its own loader over the room feed.
  let grid: ReactNode;
  if (view === 'week') {
    grid = (
      <div
        key={weekStartISO}
        data-slot="pa-calendar-week-swap"
        className={weekSwap.dir ? `cal-week-swap cal-swap-${weekSwap.dir}` : 'cal-week-swap'}
      >
        <CalendarWeekGrid
          days={weekDays}
          appointments={weekTiles}
          hours={dayListHours.length > 0 ? dayListHours : undefined}
          onAppointmentClick={handleAppointmentClick}
          // An empty-slot click opens New appointment only with its permission; silent like the By doctor double-click.
          onSlotClick={() => {
            if (canCreateAppointment) setCreateOpen(true);
          }}
          onAppointmentDrop={handleAppointmentDrop}
        />
      </div>
    );
  } else if (view === 'month') {
    grid = (
      <CalendarMonthGrid
        days={monthGrid}
        monthLabel={monthLabel}
        isLoading={isLoading}
        isError={isAppointmentsError}
        // A block chip draws read-only and opens nothing (no block availability in the PA calendar, supervisor ruling
        // 21 Oct); Month blocks come in the response's own `blockAppointment[]`, so they are not in `allRows`.
        onEventClick={(event: PACalendarMonthEvent) =>
          event.isBlock ? undefined : handleAppointmentClick(event.id)
        }
        onDayClick={(iso) => {
          setDate(iso);
          handleView('day');
        }}
      />
    );
  } else if (view === 'providers') {
    grid = (
      <PACalendarByDoctor
        doctors={doctorColumns}
        rowsByDoctor={rowsByDoctor}
        hours={dayListHours.length > 0 ? dayListHours : undefined}
        selectedDate={selectedDateObj}
        onAppointmentClick={handleAppointmentClick}
        onSlotDoubleClick={handleGridQuickCreate}
        onAppointmentDrop={handleByDoctorDrop}
      />
    );
  } else if (view === 'room') {
    grid = (
      <PACalendarByRoom
        rooms={roomColumns.length > 0 ? roomColumns : undefined}
        rowsByRoom={rowsByRoom}
        hours={dayListHours.length > 0 ? dayListHours : undefined}
        selectedDate={selectedDateObj}
        isLoading={roomsLoading}
        loadingIndicator={<BrandLoading size={48} label="Loading…" />}
        onAppointmentClick={handleAppointmentClick}
      />
    );
  } else {
    // 'day' → the Practice calendar's Day (PM `day-list-calendar.tsx`, the local `CalendarDayAgenda`), with the switch on
    // or off. Its rows carry "Open encounter" for the signed-in practitioner's visits the visit gate lets open; it fires
    // `handleRowAction(row, 'open-encounter')`, the same route and nav state as the popup's "Open encounter".
    const dayListProps = {
      rows: dayListRows,
      hours: dayListHours,
      currentHour: dayListCurrentHour,
      selectedDateISO: `${date}T00:00:00`,
      onAppointmentClick: handleAppointmentClick,
      onAppointmentAction: handleRowAction,
      ...dayListSeams,
    };
    grid = (
      <CalendarDayAgenda
        {...dayListProps}
        scrollRequest={dayScrollRequest}
        selfPractitionerId={selfId}
        onCompletePatient={(row) => void navigate(buildViewAppointmentUrl(row, undefined, true))}
      />
    );
  }

  return (
    <PACalendarPage
      toolbar={
        <>
          {pageHeader}
          {toolbarRow}
          {scopeInToolbar ? null : specialtyRow}
        </>
      }
      // PM's strip row: the floating filter pill sits in it, 8 from its top and 16 from the right, in the lane the strip
      // keeps free (`frame-scheduling.tsx` FloatingFilterPill + ProviderStrip in one relative box).
      providerStrip={
        <div className="cal-strip-row" data-slot="pa-calendar-strip-row">
          {providerStrip}
          {filterPill}
        </div>
      }
      overlays={overlays}
    >
      <div
        ref={gridRef}
        data-slot="pa-calendar-body"
        data-day-agenda={dayAgenda ? 'on' : 'off'}
        data-first-hour={dayListHours[0]}
        data-today-col={todayColumn}
      >
        {/* The loader is the page's own (not the DS frame's `loading`), so the filter pill and the popover open
            on it never unmount while a filter change refetches. Room and Month draw their own loading (PM). */}
        {isLoading && view !== 'room' && view !== 'month' ? (
          <CalendarSkeleton />
        ) : (
          grid
        )}
      </div>
    </PACalendarPage>
  );
}

/** Human-readable date+time for the reschedule confirm dialog ("Wed, Oct 22, 9:30a"), the time in the viewer's
 *  12/24-hour format (shared/lib/time-format.ts, PROD-3313). Accepts the no-Z local ISO the calendar uses;
 *  null/blank → "—". */
function formatDropTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day}, ${formatClockTime(d)}`;
}

// Re-export so the store's CalendarView type stays referenced (avoids an
// unused-import lint while documenting the page↔store view-vocab bridge).
export type { StoreCalendarView };
